import React from 'react';
import { Document, Page, Text, View, StyleSheet, Font } from '@react-pdf/renderer';
import moment from 'moment';

// Opcional: registrar una fuente con buen soporte para tildes/ñ
// Font.register({
//   family: 'Inter',
//   fonts: [
//     { src: '/fonts/Inter-Regular.ttf' },
//     { src: '/fonts/Inter-Bold.ttf', fontWeight: 'bold' },
//   ],
// });

const COLORS = {
    text: '#1F2937',        // gris muy oscuro (casi negro)
    muted: '#6B7280',       // gris medio para labels
    border: '#E5E7EB',      // gris claro para bordes/líneas
    headerBg: '#F9FAFB',    // fondo header de tabla
    white: '#FFFFFF',
};

const styles = StyleSheet.create({
    page: {
        paddingTop: 32,
        paddingBottom: 40,
        paddingHorizontal: 36,
        fontSize: 10,
        color: COLORS.text,
        fontFamily: 'Helvetica',
    },

    // ===== Header superior =====
    topBar: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        fontSize: 9,
        color: COLORS.muted,
        marginBottom: 16,
    },

    pageTitle: {
        fontSize: 14,
        fontWeight: 'bold',
        marginBottom: 12,
    },

    // ===== Caja con datos del cliente =====
    clientBox: {
        borderWidth: 1,
        borderColor: COLORS.border,
        borderRadius: 4,
        paddingVertical: 14,
        paddingHorizontal: 16,
        alignItems: 'center',
        marginBottom: 20,
    },
    clientName: {
        fontSize: 11,
        fontWeight: 'bold',
        marginBottom: 8,
    },
    clientDates: {
        flexDirection: 'row',
        fontSize: 10,
    },
    dateBlockFrom: {
        flexDirection: 'row',
        fontSize: 11,
        fontWeight: 'bold',
        marginBottom: 12,
    },
    dateBlockTo: {
        flexDirection: 'row',
        fontSize: 11,
        fontWeight: 'bold',
        marginBottom: 12,
        marginLeft: 6,
    },
    dateLabel: {
        color: COLORS.muted,
        marginRight: 6,
    },
    dateValue: {
        fontWeight: 'bold',
    },

    // ===== Sección "Cuenta completa" + totales =====
    sectionTitle: {
        fontSize: 11,
        fontWeight: 'bold',
        marginBottom: 12,
    },
    totalsBlock: {
        marginBottom: 24,
    },
    totalRow: {
        flexDirection: 'row',
        marginBottom: 4,
    },
    totalLabel: {
        width: 110,
        color: COLORS.muted,
    },
    totalValue: {
        fontWeight: 'bold',
    },

    // ===== Tabla =====
    table: {
        borderTopWidth: 1,
        borderTopColor: COLORS.border,
    },
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: COLORS.headerBg,
        paddingVertical: 8,
        paddingHorizontal: 8,
        borderBottomWidth: 1,
        borderBottomColor: COLORS.border,
    },
    tableHeaderText: {
        fontSize: 9,
        fontWeight: 'bold',
        color: COLORS.muted,
        letterSpacing: 0.5,
    },
    tableRow: {
        flexDirection: 'row',
        paddingVertical: 10,
        paddingHorizontal: 8,
        borderBottomWidth: 1,
        borderBottomColor: COLORS.border,
    },
    cellFecha: { width: '20%' },
    cellComprobante: { width: '30%' },
    cellTipo: { width: '30%' },
    cellImporte: { width: '20%', textAlign: 'right' },
});

// Helper para formatear importes estilo es-AR (1.234,56) o como prefieras
const formatAmount = (n) =>
    n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const PDFCurrectAccount = ({
    nameCustomer,
    fromDate,
    toDate,
    totalSale,
    totalPaid,
    balance,
    data,
    completeAccount,
    }) => (
    <Document>
        <Page size="A4" style={styles.page}>
        {/* Barra superior tipo "navegador" — opcional, sacala si no la querés */}
        {/*<View style={styles.topBar}>
            <Text>Cuenta Corriente</Text>
            <Text>Sistema administrativo</Text>
        </View>*/}

        {/* Caja con cliente y rango de fechas */}
        <View style={styles.clientBox}>
            <Text style={styles.clientName}>{nameCustomer}</Text>
        </View>

        {completeAccount && <Text style={styles.sectionTitle}>Cuenta completa</Text>}

        {!completeAccount && <View style={styles.clientDates}>
            <View style={styles.dateBlockFrom}>
                <Text style={styles.dateLabel}>Desde:</Text>
                <Text style={styles.dateValue}>{moment(fromDate).format('L')}</Text>
            </View>
            <View style={styles.dateBlockTo}>
                <Text style={styles.dateLabel}>Hasta:</Text>
                <Text style={styles.dateValue}>{moment(toDate).format('L')}</Text>
            </View>
        </View>}

        <View style={styles.totalsBlock}>
            <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>Total de ventas:</Text>
            <Text style={styles.totalValue}>${formatAmount(totalSale)}</Text>
            </View>
            <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>Total pagado:</Text>
            <Text style={styles.totalValue}>${formatAmount(totalPaid)}</Text>
            </View>
            <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>Saldo:</Text>
            <Text style={styles.totalValue}>${formatAmount(balance)}</Text>
            </View>
        </View>

        {/* Tabla de movimientos */}
        <View style={styles.table}>
            <View style={styles.tableHeader} fixed>
            <Text style={[styles.tableHeaderText, styles.cellFecha]}>FECHA</Text>
            <Text style={[styles.tableHeaderText, styles.cellComprobante]}>N° DE COMPROBANTE</Text>
            <Text style={[styles.tableHeaderText, styles.cellTipo]}>TIPO DE COMPROBANTE</Text>
            <Text style={[styles.tableHeaderText, styles.cellImporte]}>IMPORTE</Text>
            </View>

            {data.map((m, i) => (
            <View key={i} style={styles.tableRow} wrap={false}>
                <Text style={styles.cellFecha}>{m.formatDate}</Text>
                <Text style={styles.cellComprobante}>{m.voucherNumber}</Text>
                <Text style={styles.cellTipo}>{m.voucherLetter}</Text>
                <Text style={styles.cellImporte}>${formatAmount(m.price)}</Text>
            </View>
            ))}
        </View>
        </Page>
    </Document>
);

export default PDFCurrectAccount;